import React, { useState, useEffect } from 'react';
import { TriangleAlert } from 'lucide-react';

/**
 * ErrorAlert Component
 *
 * A reusable alert component that displays error messages with automatic timeout functionality.
 * The alert will automatically disappear after 5 seconds when an error is triggered.
 *
 * Features:
 * - Automatic visibility management based on error state
 * - Configurable title and message
 * - Automatic dismissal after 5 seconds
 * - Visual styling for error states with smooth transitions
 *
 * Props:
 * @param {boolean} isError - Whether an error state is active, triggering the alert to display
 * @param {string} [title='Error'] - The title text displayed in the alert header
 * @param {string} [message='An error occurred.'] - The detailed error message to display
 *
 * @returns {JSX.Element|null} The error alert component when visible, or null when not visible
 *
 * @example
 * // Basic usage
 * <ErrorAlert isError={hasError} />
 *
 * // With custom title and message
 * <ErrorAlert
 *   isError={submitFailed}
 *   title="Submission Failed"
 *   message="Please check your form inputs and try again."
 * />
 */

interface ErrorAlertProps {
  isError: boolean;
  title?: string;
  message?: string;
}

const ErrorAlert: React.FC<ErrorAlertProps> = ({
  isError,
  title = 'Error',
  message = 'An error occurred.',
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isError) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isError]);

  if (!isVisible) return null;

  return (
    <div className="mb-2 rounded-lg border border-red-400 bg-red-50 p-4 opacity-100 transition-all duration-1000 ease-in-out dark:border-red-700 dark:bg-red-950/40">
      <div className="flex items-center gap-1">
        <TriangleAlert className="text-error w-4 h-4" />
        <h1 className="text-error text-sm font-semibold">{title}</h1>
      </div>
      <p className="text-error text-xs font-normal">{message}</p>
    </div>
  );
};

export default ErrorAlert;
